import AppointmentUSPBlock from '@components/AppointmentUSPBlock'
import { FloatingServiceMenu, Footer, Header, Page, PageTransition } from '@components/Page'
import ProductGallery from '@components/_product-detail/ProductGallery'
import ProductInformation from '@components/_product-detail/ProductInformation'
import ProductTabs from '@components/_product-detail/ProductTabs'
import RelatedProducts from '@components/_product-detail/RelatedProducts'
import SaveRecentlyViewed from '@components/_product-detail/SaveRecentlyViewed'
import { Box } from '@gassan-ui'
import { ARTICLE } from '@graphql/article'
import { Article, ArticleVariables, Article_article } from '@graphql/__generated__/Article'
import slugify from 'slugify'
import { PageProps } from '@lib/types'
import { createUrqlClient } from '@lib/urqlClient'
import ErrorPage from '@pages/_error'
import mustache from 'mustache'
import { NextPage, NextPageContext } from 'next'
import { useRouter } from 'next/router'
import { rem } from 'polished'
import React, { useEffect } from 'react'
import { useLocalization } from '@components/Localization'
import { jewelTypeMap } from '@components/_product-detail/jewelTypeMap'
import { useWishlist } from '@components/WishlistProvider'
import * as ReactGA from 'react-ga'

type Data = {
  article: Article_article
  title: string | null
  description: string | null
  metaTitle: string | null
  metaDescription: string | null
}

let timer

const ProductDetailPage: NextPage<PageProps<Data>> = ({ statusCode, data }) => {
  const { wishlistProductIds, toggleWishlistProductId } = useWishlist()
  const { messages, lang } = useLocalization()
  const router = useRouter()
  const isPopover = typeof router.query.popover !== 'undefined'

  useEffect(() => {
    ReactGA.event({
      category: 'Ecommerce',
      action: 'productImpressions'
    })

    if (router.query.appointment) {
      timer = setTimeout(() => {
        scrollToAppointment()
      }, 500)
    }

    return () => {
      clearTimeout(timer)
    }
  }, []) //eslint-disable-line

  if (statusCode) {
    return <ErrorPage statusCode={statusCode} />
  }
  if (!data.article) {
    return <ErrorPage statusCode={404} />
  }

  let breadcrumbs = [
    {
      href: '/[...slug]',
      as: `/${slugify(data.article.brand, { lower: true })}`,
      label: data.article.brand
    },
    {
      href: '/p/[slug]',
      as: `/p/${data.article.urlSlug}`,
      label: data.article.description
    }
  ]
  const jewelTypeId = data.article.articleAttributes.find(
    attribute => attribute.attributeName === 'Type Jewel'
  )
  if (jewelTypeId) {
    const jewelTypeMapKey = jewelTypeMap[jewelTypeId.attributeValueID]

    // Add a breadcrumb if jewelTypeMapKey exists and we have a matching Kentico page in messages.urlSlugItems
    if (jewelTypeMapKey && messages.urlSlugItems[jewelTypeMapKey.urlSlugKey]) {
      breadcrumbs.unshift({
        href: '/[...slug]',
        as: `/${messages.urlSlugItems[jewelTypeMapKey.urlSlugKey]}`,
        label: jewelTypeMapKey.title[lang]
      })
    }
  }

  if (data.article.articleType === 'Horloge') {
    breadcrumbs.unshift({
      href: '/[...slug]',
      as: `/${messages.urlSlugItems.watches}`,
      label: lang === 'nl' ? 'Horloges' : 'Watches'
    })
  }

  const scrollToAppointment = () => {
    const firstInput = document.getElementsByName('name')
    const makeAppointmentRadio = document.getElementById('make-appointment') as HTMLInputElement
    const contact = document.getElementById('make-an-appointment')
    const rect = contact.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop
    if (firstInput[0] && makeAppointmentRadio && contact) {
      window.scroll({ top: rect.top + scrollTop, left: 0, behavior: 'smooth' })
      makeAppointmentRadio.checked = true
      if (firstInput[0]) {
        firstInput[0].focus({
          preventScroll: true
        })
      }
    }
  }

  const scrollToQuestion = () => {
    const firstInput = document.getElementsByName('name')
    const questionRadio = document.getElementById('ask-question') as HTMLInputElement
    const contact = document.getElementById('make-an-appointment')
    const rect = contact.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop
    if (firstInput[0] && questionRadio && contact) {
      window.scroll({ top: rect.top + scrollTop, left: 0, behavior: 'smooth' })
      questionRadio.checked = true
      if (firstInput[0]) {
        firstInput[0].focus({
          preventScroll: true
        })
      }
    }
  }

  return (
    <Page
      title={data.title}
      description={data.metaDescription}
      additionalMetaTags={[{ property: 'title', content: data.title }]}
      canonical={`/${lang}/p/${data.article.urlSlug}`}
      languageAlternates={[
        { href: `/nl/p/${data.article.urlSlug}`, hrefLang: 'nl' },
        { href: `/en/p/${data.article.urlSlug}`, hrefLang: 'en' }
      ]}
    >
      <FloatingServiceMenu />
      <SaveRecentlyViewed id={data.article.id} />
      <Header isPopover={isPopover} />
      <PageTransition>
        <Box
          as="section"
          width={{
            _: '100%',
            large: 'calc(100% - 3rem)',
            xlarge: 'calc(100% - 5.5rem)'
          }}
          display="grid"
          gridTemplateColumns={{ _: '100%', large: `auto ${rem(360)}`, xlarge: `auto ${rem(420)}` }}
          gridColumnGap={{ large: rem(90), xlarge: rem(120) }}
          mt={{ _: 0, large: '2.5rem' }}
          mb={{ _: 0, large: '3rem' }}
          maxWidth="109rem"
          mx="auto"
        >
          <ProductGallery
            id={data.article.id}
            images={data.article.images}
            title={`${data.article.brand} ${data.article.description}`}
            breadcrumbItems={breadcrumbs}
            onClickWishlist={() =>
              toggleWishlistProductId({
                id: data.article.id,
                title: data.article.brand,
                subtitle: data.article.description,
                image: data.article.images[0]
              })
            }
            isOnWishList={wishlistProductIds.includes(data.article.id)}
            variant={isPopover ? 'popover' : 'default'}
          />
          <Box
            pt={{ _: '1.5rem', small: '3rem', large: 0 }}
            px={{ _: '1rem', small: '2rem', large: 0 }}
          >
            <ProductInformation
              {...data.article}
              handleMakeAppointment={scrollToAppointment}
              handleAskQuestion={scrollToQuestion}
            />
          </Box>
        </Box>
        <ProductTabs article={data.article} description={data.description} />
        <RelatedProducts article={data.article} />
        <AppointmentUSPBlock
          mt="5rem"
          subject={`Vraag over ${data.article.brand} - ${data.article.id}`}
          gaEventLabel={lang === 'nl' ? 'ProductDetailPageNL' : 'ProductDetailPageEN'}
        />
        <Footer />
      </PageTransition>
    </Page>
  )
}

const getAttributesMap = (data: Article) => {
  const { articleAttributes, __typename, images, ...rest } = data.article
  let attributes = {}
  Object.keys(rest).forEach(key => {
    if (typeof rest[key] === 'string') {
      attributes[key] = rest[key]
    }
    if (typeof rest[key] === 'number') {
      attributes[key] = rest[key]
    }
  })
  articleAttributes.forEach(attr => {
    attributes[attr.attributeOriginalName] = attr.attributeValue
  })
  return attributes
}

ProductDetailPage.getInitialProps = async (ctx: NextPageContext) => {
  const id = (ctx.query.slug as string).split('-').pop()
  const lang = ctx.query.lang as string
  const client = createUrqlClient(lang)
  try {
    const { data } = await client
      .query<Article, ArticleVariables>(ARTICLE, { id })
      .toPromise()

    // Chopard is split into chopard-jewelry & chopard-watches
    let brandSlug = slugify(data.article.brand, { lower: true })
    if (brandSlug === 'chopard') {
      if (data.article.articleType === 'Horloge') {
        brandSlug = lang === 'en' ? 'chopard-watches' : 'chopard-horloges'
      } else {
        brandSlug = lang === 'en' ? 'chopard-jewelry' : 'chopard-juwelen'
      }
    }

    // Get file with dynamic content
    let json = {}
    try {
      json = await import(`@components/_product-detail/pdp-content/${brandSlug}-${lang}.json`).then(
        mod => mod.default
      )
    } catch (error) {
      console.error(error)
    }
    const attributes = getAttributesMap(data)
    // https://stackoverflow.com/questions/22910428/mustache-globally-disable-html-escaping/23057003#23057003
    mustache.escape = value => value
    const title = json['product_detail_title']
      ? mustache.render(json['product_detail_title'], attributes)
      : null
    const description = json['product_detail_description']
      ? mustache.render(json['product_detail_description'], attributes)
      : null
    const metaTitle = json['product_detail_meta_title']
      ? mustache.render(json['product_detail_meta_title'], attributes)
      : null
    const metaDescription = json['product_detail_meta_description']
      ? mustache.render(json['product_detail_meta_description'], attributes)
      : null

    if (!data) {
      return { statusCode: 500 }
    }
    if (!data.article) {
      return { statusCode: 404 }
    }
    return {
      data: {
        article: data.article,
        title,
        description,
        metaTitle,
        metaDescription
      }
    }
  } catch (error) {
    console.error(error)
    return { statusCode: 500 }
  }
}

export default ProductDetailPage
